/* eslint-disable react-hooks/rules-of-hooks */

import { useCallback, useEffect, useRef, useState } from 'react';

import { useGetMemberDetails } from 'core/hooks';
import { PHONE_NUMBERS_TYPE } from 'core/constants';
import { ExpMemberData } from 'core/api/client/MemberExperienceApi';

/***
 * @description Custom React hook to fetch and manage member data from the new Member Experience API.
 *
 * This hook encapsulates the logic for retrieving member details, handling loading and error states,
 * and exposing key member contact information (mobile number and email address).
 * It is designed to be highly reusable, testable, and maintainable, following modern React best practices.
 *
 * optional props of `isAuthenticated` to prevent calling useGetMemberDetails hook in
 * unauthenticated pages
 *
 * @returns {Object} An object containing:
 *   - mobileNumberApiResponse {string | undefined}: The member's mobile number if available, otherwise undefined.
 *   - emailAddressApiResponse {string}: The member's email address, or an empty string if not available.
 *   - contactDetailsObject {ExpMemberData}: The full member data object as returned by the API.
 *   - loading {boolean}: Indicates if the data is currently being fetched.
 *   - error {Error | null}: Any error encountered during the fetch operation, or null if no error.
 *   - refetch {() => Promise<void>}: Function to manually trigger a data refresh.
 *
 * @example
 * const {
 *   mobileNumberApiResponse,
 *   emailAddressApiResponse,
 *   contactDetailsObject,
 *   loading,
 *   error,
 *   refetch,
 * } = useGetMemberData();
 *
 * @todo Optimize this hook in order to avoid multiple calls from the same component. Ex: OTP component and Combine Super
 */
export const useGetMemberData = (isAuthenticated = true) => {
	const [newMemberData, setNewMemberData] = useState<ExpMemberData>({});
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<Error | null>(null);
	const isMounted = useRef(true);

	const fetchNewMemExData = useCallback(async () => {
		setLoading(true);
		setError(null);

		// prevent calling useGetMemberDetails hook if unauthenticated
		// (especially in unathenticated pages calling OneTimePasscode component)
		if (!isAuthenticated) {
			return;
		}

		try {
			const memberExperienceData = await useGetMemberDetails();
			if (isMounted.current) {
				setNewMemberData(memberExperienceData as ExpMemberData);
			}
		} catch (err) {
			if (isMounted.current) {
				setNewMemberData({} as ExpMemberData);
				setError(err instanceof Error ? err : new Error('Unknown error'));
				console.error('Error fetching member details:', err);
			}
		} finally {
			if (isMounted.current) setLoading(false);
		}
	}, []);

	useEffect(() => {
		isMounted.current = true;
		fetchNewMemExData();

		return () => {
			isMounted.current = false;
		};
	}, [fetchNewMemExData]);

	const refetch = useCallback(async () => {
		await fetchNewMemExData();
	}, [fetchNewMemExData]);

	const memberContext = newMemberData ?? {};

	const contactDetailsObject = memberContext;

	const mobileObj = contactDetailsObject?.data?.phone_numbers?.find(
		(phone) =>
			phone.type === PHONE_NUMBERS_TYPE.MOBILE &&
			phone.number !== '' &&
			phone.number !== null,
	);

	const mobileNumberApiResponse = !!mobileObj ? mobileObj?.number : undefined;
	const emailAddressApiResponse =
		contactDetailsObject?.data?.email_address ?? '';

	return {
		mobileNumberApiResponse,
		emailAddressApiResponse,
		contactDetailsObject,
		loading,
		error,
		refetch,
	};
};
